import { translate } from 'components/Translation';
import modelsResolver from 'utils/api/models/utils/modelsResolver';
import { convertGetUsageItemsToEcommerce } from 'utils/ecommerce/services/extract';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import { tariffPlan } from 'utils/api/models/utils/tariffPlan';
import { AccountType } from 'utils/api/models/accounts/accountType';

export type UsageGroup = 'GENERAL' | 'ROAMING' | 'SPECIAL' | 'BONUS';
export type UsageType = 'UNKNOWN' | 'DATA' | 'BALANCE' | 'VOICE' | 'SMS';

export interface UsageValue {
	value: number;
	unit: string;
}

export interface Product {
	group: string;
	name: string;
	productId: string;
	startDate: string;
	endDate: string;
}

interface TotalProtoype {
	group: UsageGroup;
	type: UsageType;
	unlimited: boolean;
	unit?: string;
}

interface InputTotal extends TotalProtoype {
	totalBalance?: number;
	totalConsumption?: number;
	totalPackageSize?: number;
	totalExtraUsage?: number;
}

export interface Total extends TotalProtoype {
	totalBalance: UsageValue;
	totalConsumption: UsageValue;
	totalPackageSize: UsageValue;
	totalExtraUsage: UsageValue;
	usedPercentage: number;
}

export interface Balance {
	mainBalance?: number;
	postpaidCreditLimit?: number;
	postpaidBalance?: number;
}

export interface InputCommerceAttribute {
	name: string;
	values: string[];
}

interface PackagePrototype {
	packageId: string;
	name: string;
	type: UsageType;
	group: UsageGroup;
	usageType: string;
	commerceUniqueId?: string;
	commerceUniqueIdToActivate?: any;
	limited: boolean;
	renewable: boolean;
	validFrom: string;
	validTo: string;
	status?: string;
	bundleStatus?: string;
	unit?: string;
	payg: boolean;
	plan?: any;
	daysLeft?: any;
	creditAvailable: boolean;
}

interface InputPackage extends PackagePrototype {
	balance?: number;
	consumption?: number;
	packageSize?: number;
	totalPackageSize?: number;
	commerceAttributes?: InputCommerceAttribute[];
}

export interface Package extends PackagePrototype {
	balance: UsageValue;
	consumption: UsageValue;
	packageSize: UsageValue;
	totalPackageSize: UsageValue;
	usedPercentage: number;
	commerceAttributes: SinglePackage;
}
export interface UsageResponse {
	packages: InputPackage[];
	totals: InputTotal[];
	balance: Balance;
	lineExpiryDate?: string;
}

export type Usage = {
	_packages: InputPackage[];
	packages: Package[];
	_totals: InputTotal[];
	totals: Total[];
	balance: Balance;
	products: Product[];
	lineExpiryDate?: string;
	bonusBalance: number;
	mainBalance: number;
	tariffPlan: (accountType: AccountType) => Product | undefined;
};

const usedPercentage = (numerator: number | undefined, denominator: number | undefined): number => {
	return ((numerator || 0) * 100) / (denominator || 0);
};

const round = (value: number, precision: number) => {
	const multiplier = Math.pow(10, precision || 0);
	return Math.round(value * multiplier) / multiplier;
};

const usageUnitReadable = (value?: number, unit?: string) => {
	value = value || 0;
	if (unit === 'MB' && value >= 1000) {
		unit = 'GB';
	}
	if (!unit) {
		return { value: round(value, 1), unit: translate('usage.unit.none') };
	}
	if (!!unit && unit === 'GB') {
		return { value: round(value / 1024, 1), unit: translate(`usage.unit.${unit}`) };
	} else {
		return { value: round(value, 1), unit: translate(`usage.unit.${unit}`) };
	}
};

const packageUsedPercentage = (usagePackage: InputPackage) =>
	usedPercentage(usagePackage.consumption, usagePackage.totalPackageSize);

const totalUsedPercentage = (total: InputTotal) => usedPercentage(total.totalConsumption, total.totalPackageSize);

const inputPackageToParsedPackage: (input: InputPackage) => Package = (input: InputPackage) => {
	let tmpOutput: any = {};
	Object.assign(tmpOutput, input);
	let output: Package = tmpOutput as Package;
	output.balance = usageUnitReadable(input.balance, input.unit);
	output.consumption = usageUnitReadable(input.consumption, input.unit);
	output.packageSize = usageUnitReadable(input.packageSize, input.unit);
	output.totalPackageSize = usageUnitReadable(input.totalPackageSize, input.unit);
	output.usedPercentage = packageUsedPercentage(input);
	output.commerceAttributes = {} as SinglePackage;
	if (input.commerceAttributes) {
		const inputCommerceAttributes = input.commerceAttributes.filter(
			(commerceAttribute) => commerceAttribute.values
		);
		const inputCommerceAttributesAsEcommerce = convertGetUsageItemsToEcommerce(inputCommerceAttributes);
		output.commerceAttributes = modelsResolver(
			{ entry: inputCommerceAttributesAsEcommerce },
			'singlePackage'
		) as SinglePackage;
	}
	return output;
};

const inputTotalToParsedTotal: (input: InputTotal) => Total = (input: InputTotal) => {
	let tmpOutput: any = {};
	Object.assign(tmpOutput, input);
	let output: Total = tmpOutput as Total;
	output.totalBalance = usageUnitReadable(input.totalBalance, input.unit);
	output.totalConsumption = usageUnitReadable(input.totalConsumption, input.unit);
	output.totalPackageSize = usageUnitReadable(input.totalPackageSize, input.unit);
	output.totalExtraUsage = usageUnitReadable(input.totalExtraUsage, input.unit);
	output.usedPercentage = totalUsedPercentage(input);
	return output;
};

const model: types.models.InputModel = {
	_packages: ['response.getUsageResponse.packages', []],
	_totals: ['response.getUsageResponse.totals', []],
	products: ['response.getUsageResponse.products', []],
	balance: [
		'response.getUsageResponse.balance',
		{
			mainBalance: undefined,
		},
	],
	lineExpiryDate: 'response.getUsageResponse.lineExpiryDate',
	bonusBalance: function(model: Usage): number | undefined {
		if (model._totals) {
			const total = model._totals.find((total) => total.group === 'BONUS' && total.type === 'BALANCE');
			if (total) {
				return total.totalBalance;
			}
		}
		return undefined;
	},
	mainBalance: function(model: Usage): number | undefined {
		return model.balance.mainBalance;
	},
	packages: function(model: Usage): Package[] {
		return model._packages.map(inputPackageToParsedPackage);
	},
	totals: function(model: Usage): Total[] {
		return model._totals.map(inputTotalToParsedTotal);
	},
	tariffPlan: function(model: Usage) {
		return (accountType: AccountType) => tariffPlan(model.products, 'group', accountType);
	},
};

export default model;
